import { EnvelopeIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { cn } from '@/lib/utils';
import type { TreatmentRecordRequestChannelSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { isEmailValid, isPhoneNumberValid } from '@/utils/validation';

type ChannelType = 'fax' | 'email';

interface InlineChannelUpdateFormProps {
  matterId: string;
  providerId: string;
  requestId: string;
  channelType: ChannelType;
  currentChannel: TreatmentRecordRequestChannelSchema | null | undefined;
  onSuccess: () => void;
}

/**
 * Inline form for updating channel contact info (fax number or email).
 * Clean, minimal design for adding missing contact information.
 */
export const InlineChannelUpdateForm = ({
  matterId,
  providerId,
  requestId,
  channelType,
  currentChannel,
  onSuccess,
}: InlineChannelUpdateFormProps) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [createChannel, { isLoading: isCreating }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsCreateMutation();
  const [updateChannel, { isLoading: isUpdating }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChannelsUpdateMutation();

  const isLoading = isCreating || isUpdating;

  const validate = useCallback(
    (val: string): boolean => {
      if (!val.trim()) {
        setError(
          channelType === 'fax'
            ? 'Fax number is required'
            : 'Email address is required',
        );
        return false;
      }

      if (channelType === 'fax') {
        if (!isPhoneNumberValid(val)) {
          setError('Please enter a valid fax number');
          return false;
        }
      } else if (!isEmailValid(val)) {
        setError('Please enter a valid email address');
        return false;
      }

      setError(null);
      return true;
    },
    [channelType],
  );

  const handleSave = useCallback(async () => {
    if (!validate(value)) return;

    try {
      if (currentChannel?.id) {
        await updateChannel({
          matterId,
          providerId,
          requestId,
          channelId: currentChannel.id,
          updateTreatmentRecordRequestChannelSchema: {
            type: channelType,
            ...(channelType === 'fax' ? { fax: value } : { email: value }),
          },
        }).unwrap();
      } else {
        await createChannel({
          matterId,
          providerId,
          requestId,
          createTreatmentRecordRequestChannelSchema: {
            type: channelType,
            ...(channelType === 'fax' ? { fax: value } : { email: value }),
          },
        }).unwrap();
      }

      toast.success(
        `${channelType === 'fax' ? 'Fax number' : 'Email address'} saved`,
      );
      onSuccess();
    } catch (err) {
      console.error('Failed to save channel:', err);
      toast.error('Failed to save contact information');
    }
  }, [
    value,
    validate,
    currentChannel,
    matterId,
    providerId,
    requestId,
    channelType,
    createChannel,
    updateChannel,
    onSuccess,
  ]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      if (error) {
        setError(null);
      }
    },
    [error],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !isLoading) {
        e.preventDefault();
        handleSave();
      }
    },
    [handleSave, isLoading],
  );

  const Icon = channelType === 'fax' ? PrinterIcon : EnvelopeIcon;

  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-4">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
          <Icon className="h-5 w-5 text-zinc-500" />
        </div>

        {/* Form */}
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-sm font-medium text-zinc-900">
            Add {channelType === 'fax' ? 'fax number' : 'email address'}
          </p>

          <div className="flex gap-2">
            <input
              id={`inline-${channelType}-input`}
              type={channelType === 'email' ? 'email' : 'tel'}
              value={value}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={
                channelType === 'fax'
                  ? '(555) 123-4567'
                  : 'records@provider.com'
              }
              className={cn(
                'flex-1 rounded-lg border px-3 py-2 text-sm transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-offset-1',
                error
                  ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
                  : 'border-zinc-200 focus:border-indigo-400 focus:ring-indigo-100',
              )}
              disabled={isLoading}
            />
            <Button
              color="indigo"
              onClick={handleSave}
              disabled={isLoading || !value.trim()}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Save'
              )}
            </Button>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      </div>
    </div>
  );
};
